import type { DataLoadStatus, IndicatorSeries } from "../types";
import type { CoreDataBundle } from "../data/loaders/coreData";
import type { SituationAnalysis } from "../data/situationAnalysis";
import { sourceRegistry } from "../data/sourceRegistry.ts";
import { ECOS_SERIES } from "../data/ecosCatalog.ts";
import { monthlyRateRows } from "../data/housingTransmissionSeries.ts";
import { collectH1bEcosMetadataAudit, failedH1bMetadataAudit, type H1bEcosMetadataAudit } from "../data/h1bEcosMetadataAudit.ts";
import { getRuntimeDiagnosticSnapshot } from "./runtimeDiagnostics.ts";
import { HOUSING_DETAILED_CHART_SCOPES } from "../data/housingHtsRegistry.ts";

const encoder = new TextEncoder();

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let i = 0; i < 8; i++) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function write16(view: DataView, offset: number, value: number) { view.setUint16(offset, value, true); }
function write32(view: DataView, offset: number, value: number) { view.setUint32(offset, value >>> 0, true); }

function dosDateTime(date: Date) {
  const year = Math.max(1980, date.getFullYear());
  const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { dosTime, dosDate };
}

interface ZipEntry {
  name: string;
  bytes: Uint8Array;
}

function concat(parts: Uint8Array[]) {
  const size = parts.reduce((sum, part) => sum + part.byteLength, 0);
  const out = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) { out.set(part, offset); offset += part.byteLength; }
  return out;
}

function buildStoreZip(entries: ZipEntry[]) {
  const now = new Date();
  const { dosTime, dosDate } = dosDateTime(now);
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let localOffset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name.replace(/\\/g, "/"));
    const crc = crc32(entry.bytes);
    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    write32(lv, 0, 0x04034b50);
    write16(lv, 4, 20);
    write16(lv, 6, 0x0800);
    write16(lv, 8, 0);
    write16(lv, 10, dosTime);
    write16(lv, 12, dosDate);
    write32(lv, 14, crc);
    write32(lv, 18, entry.bytes.length);
    write32(lv, 22, entry.bytes.length);
    write16(lv, 26, nameBytes.length);
    write16(lv, 28, 0);
    local.set(nameBytes, 30);
    localParts.push(local, entry.bytes);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    write32(cv, 0, 0x02014b50);
    write16(cv, 4, 20);
    write16(cv, 6, 20);
    write16(cv, 8, 0x0800);
    write16(cv, 10, 0);
    write16(cv, 12, dosTime);
    write16(cv, 14, dosDate);
    write32(cv, 16, crc);
    write32(cv, 20, entry.bytes.length);
    write32(cv, 24, entry.bytes.length);
    write16(cv, 28, nameBytes.length);
    write16(cv, 30, 0);
    write16(cv, 32, 0);
    write16(cv, 34, 0);
    write16(cv, 36, 0);
    write32(cv, 38, 0);
    write32(cv, 42, localOffset);
    central.set(nameBytes, 46);
    centralParts.push(central);
    localOffset += local.length + entry.bytes.length;
  }

  const centralStart = localOffset;
  const centralBytes = concat(centralParts);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  write32(ev, 0, 0x06054b50);
  write16(ev, 4, 0);
  write16(ev, 6, 0);
  write16(ev, 8, entries.length);
  write16(ev, 10, entries.length);
  write32(ev, 12, centralBytes.length);
  write32(ev, 16, centralStart);
  write16(ev, 20, 0);
  return concat([...localParts, centralBytes, eocd]);
}

function json(value: unknown) {
  return encoder.encode(`${JSON.stringify(value, null, 2)}\n`);
}

function csvCell(value: unknown) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function seriesCsv(series: IndicatorSeries) {
  const lines = ["date,value"];
  for (const obs of series.observations) lines.push([obs.date, obs.value].map(csvCell).join(","));
  return encoder.encode(`\uFEFF${lines.join("\n")}\n`);
}

function safeFilename(value: string) {
  return value.replace(/[^0-9A-Za-z가-힣._-]+/g, "_").slice(0, 120);
}

function firstNumericDate(series: IndicatorSeries) {
  return series.observations.find((o) => o.value != null)?.date ?? null;
}

function latestNumericDate(series: IndicatorSeries) {
  return [...series.observations].reverse().find((o) => o.value != null)?.date ?? null;
}

function allSeries(bundle: CoreDataBundle | null) {
  if (!bundle) return [] as IndicatorSeries[];
  const list = [
    ...Object.values(bundle.series),
    bundle.policyGap,
    bundle.curve103,
    bundle.creditSpread,
    bundle.fundingMomentum,
    bundle.corpLoanFundingSpread,
    bundle.mortgageFundingSpread,
    bundle.ktb5Monthly,
    bundle.koribor12Monthly,
    bundle.krUs2Spread,
    bundle.krUs3Spread,
    bundle.bokFedSpread,
    ...Object.values(bundle.legacyMortgage.inputs),
    bundle.legacyMortgage.lockInSpread,
    bundle.legacyMortgage.repricingPressure,
    bundle.legacyMortgage.aggregateAnnualInterestProxy,
    bundle.legacyMortgage.cashFlowStressProxy
  ];
  return Array.from(new Map(list.map((s) => [s.id, s])).values());
}

function resourceDiagnostics() {
  if (typeof performance === "undefined") return [];
  return performance.getEntriesByType("resource")
    .filter((entry) => entry.name.includes("/api/"))
    .map((entry) => {
      const timing = entry as PerformanceResourceTiming;
      const url = new URL(timing.name, window.location.href);
      return {
        resource: `${url.pathname}${url.search}`,
        durationMs: Number(timing.duration.toFixed(1)),
        transferSize: timing.transferSize,
        encodedBodySize: timing.encodedBodySize,
        decodedBodySize: timing.decodedBodySize,
        initiatorType: timing.initiatorType
      };
    });
}

export function createDiagnosticZip(args: {
  bundle: CoreDataBundle | null;
  status: DataLoadStatus;
  appVersion: string;
  selectedRange?: string;
  h1bMetadataAudit?: H1bEcosMetadataAudit | null;
  situationAnalysis?: SituationAnalysis;
  housingChartFiles?: Array<{ name: string; payload: unknown }>;
}) {
  const generatedAt = new Date().toISOString();
  const series = allSeries(args.bundle);
  const entries: ZipEntry[] = [];

  const readme = [
    "금융→실물경제 충격 전이 모니터 진단 번들",
    `앱 버전: ${args.appVersion}`,
    `생성시각: ${generatedAt}`,
    "",
    "이 ZIP은 ChatGPT 등에 업로드해 데이터 연결/계산/상태 오류를 재현·검토하기 위한 진단용입니다.",
    "ECOS API key, 쿠키, localStorage 내용은 포함하지 않습니다.",
    "",
    "주요 파일:",
    "- manifest.json: 브라우저/앱/화면 상태(비밀정보 제외)",
    "- status.json / issues.json: 데이터 로더 상태와 오류",
    "- series-index.json: 모든 원/파생 계열의 메타데이터와 관측 범위",
    "- series/*.csv: 현재 브라우저에 적재된 원/파생 시계열",
    "- lag-analysis.json: 금융→대출 0~6개월 시차상관 + 즉시/단기/지연 전이구간 요약",
    "- events.json: 정책변경/0선교차 이벤트",
    "- api-resource-timing.json: 동일 출처 /api 요청의 브라우저 timing(HTTP body/secret 없음)",
    "- frontend/runtime-errors.json: React ErrorBoundary·window error·unhandled rejection·사용자 작업 오류(PNG export 등)의 message/stack/componentStack과 HTS 선택상태",
    "- source-registry.json: 코드에 등록된 데이터원 상태",
    "- analysis/situation-analysis.json: Overview의 deterministic 현재상황·3M/6M/12M 방향압력 판정",
    "- housing/: 정책레짐·가격구간·대출가능여력·H1B Legacy Mortgage·H2 연결상태",
    "- housing/chart/: HTS 화면이 실제 사용하는 RTMS 차트 index/scope JSON snapshot(가능한 경우)",
    "- housing/relationship-regime-analysis.json: H4.6 기간/정책국면/rolling 구조변화 + H4.7 정·역방향, null envelope, 가설/발견 결과",
    "- housing/local-projection-analysis.json: H4.8 0~12M 누적반응, residual innovation proxy, lagged common controls, HAC 구간",
    "- housing/identified-shock-local-projection.json: H4.9 market-event/external shock별 LP 결과",
    "- housing/shock-identification.json: H4.9 정책 이벤트 proxy와 외부 shock strict gate 상태",
    "- housing/pit-replay-audit.json: H4.9 PIT certified/proxy/blocked 및 as-of replay",
    "- housing/market-state-analysis.json: H4.10 Price×Volume 상태·지속 run·as-of 전이·divergence·thin-market 신뢰도",
    "- housing/nonlinear-transmission.json: H4.10 금리 인상/인하 비대칭 + as-of 상대 금리수준 interaction + exact 정책제약 interaction",
    "- housing/hysteresis-analysis.json: H4.10 동일 금리수준 인상기/인하기 경로의존성",
    "- housing/rate-threshold-kink-analysis.json: H4.10 절대 주담대금리 cutoff 전수탐색 + block-permutation max-statistic",
    "- housing/spatial-transmission-analysis.json: H4.11 서울↔경기 양방향 spatial lead-lag + 공통금리 민감도",
    "- housing/market-state-change-point-analysis.json: H4.12 가격×거래 시장상태 retrospective break-month 전수탐색 + block-permutation max-statistic",
    "- housing/analysis-data-capabilities.json: 현재 API/백필/향후축적/신규소스 필요 구분",
    "- policy/policy-watch.json: 공식 소스 Auto-Watcher 원상태와 미검증 정책 후보",
    "",
    "주의: N/A/STALE은 오류가 아니라 의도된 데이터 품질 상태일 수 있습니다. issues.json과 series-index.json을 함께 확인하세요."
  ].join("\n");
  entries.push({ name: "README.txt", bytes: encoder.encode(`${readme}\n`) });

  const browser = typeof window === "undefined" ? null : {
    page: `${window.location.origin}${window.location.pathname}${window.location.hash}`,
    selectedRange: args.selectedRange ?? null,
    viewport: { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio },
    language: navigator.language,
    userAgent: navigator.userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
  entries.push({ name: "manifest.json", bytes: json({ appVersion: args.appVersion, generatedAt, browser, privacy: { apiKeys: false, cookies: false, localStorage: false } }) });
  entries.push({ name: "status.json", bytes: json(args.status) });
  entries.push({ name: "issues.json", bytes: json(args.status.issues) });
  entries.push({ name: "source-registry.json", bytes: json(sourceRegistry) });
  entries.push({ name: "api-resource-timing.json", bytes: json(typeof window === "undefined" ? [] : resourceDiagnostics()) });
  entries.push({ name: "frontend/runtime-errors.json", bytes: json(getRuntimeDiagnosticSnapshot()) });
  if (args.situationAnalysis) entries.push({ name: "analysis/situation-analysis.json", bytes: json(args.situationAnalysis) });
  if (args.h1bMetadataAudit) entries.push({ name: "housing/h1b-ecos-metadata-audit.json", bytes: json(args.h1bMetadataAudit) });

  entries.push({
    name: "series-index.json",
    bytes: json(series.map((s) => ({
      id: s.id,
      name: s.name,
      shortName: s.shortName,
      layer: s.layer,
      unit: s.unit,
      observationCount: s.observations.length,
      numericObservationCount: s.observations.filter((o) => o.value != null).length,
      firstDate: s.observations[0]?.date ?? null,
      firstNumericDate: firstNumericDate(s),
      lastDate: s.observations.at(-1)?.date ?? null,
      latestNumericDate: latestNumericDate(s),
      source: s.source
    })))
  });

  for (const s of series) entries.push({ name: `series/${safeFilename(s.id)}.csv`, bytes: seriesCsv(s) });
  entries.push({ name: "lag-analysis.json", bytes: json(args.bundle?.lagScans ?? []) });
  entries.push({ name: "housing/housing-lead-lag.json", bytes: json(args.bundle?.housingLagScans ?? []) });
  entries.push({ name: "housing/relationship-regime-analysis.json", bytes: json(args.bundle?.housingRelationshipAnalysis ?? null) });
  entries.push({ name: "housing/local-projection-analysis.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.localProjections ?? []) });
  entries.push({ name: "housing/identified-shock-local-projection.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.identifiedShockLocalProjections ?? []) });
  entries.push({ name: "housing/shock-identification.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.shockIdentification ?? null) });
  entries.push({ name: "housing/pit-replay-audit.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.pitReplayAudit ?? null) });
  entries.push({ name: "housing/market-state-analysis.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.marketStates ?? null) });
  entries.push({ name: "housing/nonlinear-transmission.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.nonlinearTransmission ?? null) });
  entries.push({ name: "housing/hysteresis-analysis.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.hysteresis ?? null) });
  entries.push({ name: "housing/rate-threshold-kink-analysis.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.rateThresholdKink ?? null) });
  entries.push({ name: "housing/spatial-transmission-analysis.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.spatialTransmission ?? null) });
  entries.push({ name: "housing/market-state-change-point-analysis.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.marketStateChangePoints ?? null) });
  entries.push({ name: "housing/analysis-data-capabilities.json", bytes: json(args.bundle?.housingRelationshipAnalysis?.dataCapabilityMatrix ?? []) });
  entries.push({ name: "policy/policy-watch.json", bytes: json(args.bundle?.policyWatch ?? null) });
  entries.push({ name: "events.json", bytes: json(args.bundle?.events ?? []) });

  if (args.bundle?.housing) {
    const housing = args.bundle.housing;
    entries.push({ name: "housing/policy-regimes.json", bytes: json({ asOfDate: housing.asOfDate, activeRegimeId: housing.activeRegimeId, regimes: housing.regimes, activeRegime: housing.activeRegime }) });
    entries.push({ name: "housing/price-band-summary.json", bytes: json({ policyBands: housing.policyBands, examplePricesEok: housing.examplePricesEok, transactionModule: housing.transactionModule }) });
    for (const profile of housing.profiles) {
      const lines = ["price_eok,allowed_mortgage_eok,capacity_pct,equity_eok,band_id,binding_constraint"];
      for (const row of profile.curve) lines.push([row.priceEok, row.allowedMortgageEok, row.capacityPct, row.equityEok, row.bandId, row.bindingConstraint].map(csvCell).join(","));
      entries.push({ name: `housing/mortgage_capacity_${safeFilename(profile.region.id)}.csv`, bytes: encoder.encode(`\uFEFF${lines.join("\n")}\n`) });
    }
    const fundingNew = args.bundle.series[ECOS_SERIES.fundingNew.id];
    const mortgageLoan = args.bundle.series[ECOS_SERIES.mortgageLoan.id];
    if (fundingNew && mortgageLoan) {
      const rateRows = monthlyRateRows([
        { key: "ktb5_monthly_avg_pct", series: args.bundle.ktb5Monthly },
        { key: "funding_new_pct", series: fundingNew },
        { key: "mortgage_new_pct", series: mortgageLoan }
      ]);
      const rateLines = ["month,ktb5_monthly_avg_pct,funding_new_pct,mortgage_new_pct"];
      for (const row of rateRows) rateLines.push([row.month, row.ktb5_monthly_avg_pct, row.funding_new_pct, row.mortgage_new_pct].map(csvCell).join(","));
      entries.push({ name: "housing/rate-transmission-monthly.csv", bytes: encoder.encode(`\uFEFF${rateLines.join("\n")}\n`) });
    }

    entries.push({ name: "housing/housing-interpretation.json", bytes: json({ observed: "현재 가격대별 주담대 절대한도와 등록된 LTV 규칙을 계산합니다.", mechanism: "가격 대비 허용 대출비중이 낮을수록 대출금리 직접경로가 약해질 수 있으나 자기자본 기회비용 경로는 남습니다.", inferenceGuard: "금리상승만으로 가격하락을 판정하지 않으며 거래·가격 원자료가 연결되기 전에는 H3/H4 반응을 N/A로 유지합니다." }) });
    const legacy = args.bundle.legacyMortgage;
    entries.push({
      name: "housing/legacy-mortgage-status.json",
      bytes: json({
        status: legacy.status, gates: legacy.gates, snapshots: legacy.snapshots, issues: legacy.issues, interpretation: legacy.interpretation,
        inputSources: Object.fromEntries(Object.entries(legacy.inputs).map(([key, value]) => [key, value.source])),
        expectedInflationSelectionPolicy: { mode: "runtime_user_selectable", defaultView: "COMPARE", options: ["1Y", "3Y", "5Y"], note: "All exact horizons are preserved. No fixed horizon is hard-coded into diagnostics or the base bundle." },
        expectedInflationCandidates: Object.fromEntries(Object.entries(legacy.expectedInflationCandidates).map(([key, value]) => [key, value?.source ?? null]))
      })
    });
    const legacyRows = monthlyRateRows([
      { key: "new_mortgage_rate_pct", series: legacy.inputs.newMortgageRate },
      { key: "existing_mortgage_cost_pct", series: legacy.inputs.existingMortgageCost },
      { key: "lock_in_spread_pp", series: legacy.lockInSpread },
      { key: "repricing_exposure_pct", series: legacy.inputs.repricingExposure },
      { key: "repricing_pressure_pp", series: legacy.repricingPressure },
      { key: "mortgage_balance_trillion_krw", series: legacy.inputs.mortgageBalance },
      { key: "annual_interest_proxy_trillion_krw", series: legacy.aggregateAnnualInterestProxy },
      { key: "expected_inflation_1y_pct", series: legacy.expectedInflationCandidates.oneYear ?? legacy.inputs.expectedInflation },
      { key: "expected_inflation_3y_pct", series: legacy.expectedInflationCandidates.threeYear ?? legacy.inputs.expectedInflation },
      { key: "expected_inflation_5y_pct", series: legacy.expectedInflationCandidates.fiveYear ?? legacy.inputs.expectedInflation }
    ]);
    const legacyLines = ["month,new_mortgage_rate_pct,existing_mortgage_cost_pct,lock_in_spread_pp,repricing_exposure_pct,repricing_pressure_pp,mortgage_balance_trillion_krw,annual_interest_proxy_trillion_krw,expected_inflation_1y_pct,expected_inflation_3y_pct,expected_inflation_5y_pct"];
    for (const row of legacyRows) legacyLines.push([row.month, row.new_mortgage_rate_pct, row.existing_mortgage_cost_pct, row.lock_in_spread_pp, row.repricing_exposure_pct, row.repricing_pressure_pp, row.mortgage_balance_trillion_krw, row.annual_interest_proxy_trillion_krw, row.expected_inflation_1y_pct, row.expected_inflation_3y_pct, row.expected_inflation_5y_pct].map(csvCell).join(","));
    entries.push({ name: "housing/legacy-mortgage-monthly.csv", bytes: encoder.encode(`\uFEFF${legacyLines.join("\n")}\n`) });
    entries.push({ name: "housing/lock-in-spread.csv", bytes: seriesCsv(legacy.lockInSpread) });
    entries.push({
      name: "housing/mortgage-repricing-analysis.json",
      bytes: json({
        repricingStatus: legacy.status.repricingLabel, cashFlowStressStatus: legacy.status.cashFlowStressLabel, autoClassification: legacy.status.classification, classificationReason: legacy.status.classificationReason,
        formulas: { lockInSpread: "new mortgage rate - existing mortgage cost (same month exact match)", repricingPressure: "month-over-month change in existing mortgage cost × existing mortgage stock repricing exposure", aggregateAnnualInterestProxy: "mortgage balance × existing mortgage cost" },
        guardrails: ["No new-origination fixed/variable share is substituted for existing mortgage stock exposure.", "No total outstanding loan rate is substituted for an outstanding mortgage rate.", "Missing months remain N/A; no forward-fill/interpolation is used.", "Lock-in spread is not converted into a move/sale probability.", "Cash-flow stress score and LOW/MID/HIGH thresholds remain N/A until definition and historical validation are completed."]
      })
    });
    const rtms = housing.transactionModule.rtms;
    if (rtms) {
      entries.push({ name: "housing/rtms-status.json", bytes: json({ status: rtms.status, generatedAt: rtms.generatedAt, sourceGenerationId: (rtms as any).sourceGenerationId ?? null, normalizedGenerationId: (rtms as any).normalizedGenerationId ?? null, collectorCodeSha256: (rtms as any).collectorCodeSha256 ?? null, coverage: rtms.coverage, collection: rtms.collection, quality: rtms.quality, source: rtms.source }) });
      entries.push({ name: "housing/rtms-analysis-bands.json", bytes: json(rtms.analysisBands ?? []) });
      for (const [scope, rows] of Object.entries(rtms.scopes)) {
        const txLines = ["month,band_id,band_label,transaction_count,transaction_share_pct"];
        const priceLines = ["month,band_id,band_label,median_price_proxy_krw,price_proxy_method"];
        for (const row of rows) {
          txLines.push([row.month, row.bandId, row.bandLabel, row.transactionCount, row.transactionSharePct].map(csvCell).join(","));
          priceLines.push([row.month, row.bandId, row.bandLabel, row.medianPriceProxyKrw, row.priceProxyMethod].map(csvCell).join(","));
        }
        entries.push({ name: `housing/transactions_by_band_${safeFilename(scope)}.csv`, bytes: encoder.encode(`\uFEFF${txLines.join("\n")}\n`) });
        entries.push({ name: `housing/price_by_band_${safeFilename(scope)}.csv`, bytes: encoder.encode(`\uFEFF${priceLines.join("\n")}\n`) });
      }
      if (rtms.marketAnalysis) {
        entries.push({ name: "housing/rtms-market-status.json", bytes: json({ status: rtms.marketAnalysis.status, coverage: rtms.marketAnalysis.coverage, quality: rtms.marketAnalysis.quality, fixedCellAnalysis: rtms.marketAnalysis.fixedCellAnalysis ?? null }) });
        entries.push({ name: "housing/rtms-market-bands.json", bytes: json(rtms.marketAnalysis.bands) });
        entries.push({ name: "housing/rtms-market-tail-bands.json", bytes: json(rtms.marketAnalysis.tailBands) });
        for (const [scope, rows] of Object.entries(rtms.marketAnalysis.scopes)) {
          const txLines = ["month,band_id,band_label,transaction_count,transaction_share_pct"];
          const priceLines = ["month,band_id,band_label,median_price_proxy_krw,price_proxy_method"];
          for (const row of rows) {
            txLines.push([row.month, row.bandId, row.bandLabel, row.transactionCount, row.transactionSharePct].map(csvCell).join(","));
            priceLines.push([row.month, row.bandId, row.bandLabel, row.medianPriceProxyKrw, row.priceProxyMethod].map(csvCell).join(","));
          }
          entries.push({ name: `housing/market_transactions_${safeFilename(scope)}.csv`, bytes: encoder.encode(`\uFEFF${txLines.join("\n")}\n`) });
          entries.push({ name: `housing/market_price_${safeFilename(scope)}.csv`, bytes: encoder.encode(`\uFEFF${priceLines.join("\n")}\n`) });
        }
        for (const [scope, rows] of Object.entries(rtms.marketAnalysis.tailScopes)) {
          const lines = ["month,band_id,band_label,transaction_count,transaction_share_pct,median_price_proxy_krw"];
          for (const row of rows) lines.push([row.month, row.bandId, row.bandLabel, row.transactionCount, row.transactionSharePct, row.medianPriceProxyKrw].map(csvCell).join(","));
          entries.push({ name: `housing/market_tail_60_${safeFilename(scope)}.csv`, bytes: encoder.encode(`\uFEFF${lines.join("\n")}\n`) });
        }
      }
    }
  }

  for (const file of args.housingChartFiles ?? []) entries.push({ name: `housing/chart/${safeFilename(file.name)}`, bytes: json(file.payload) });
  return new Blob([buildStoreZip(entries)], { type: "application/zip" });
}

async function collectHousingChartDiagnostics() {
  const files: Array<{ name: string; payload: unknown }> = [];
  const errors: Array<{ resource: string; error: string }> = [];
  try {
    const { DAILY_ANALYSIS_ENABLED } = await import("../data/dailyAnalysisConfig.ts");
    if (DAILY_ANALYSIS_ENABLED) {
      const { dailyAnalysisClient } = await import("../data/dailyAnalysisClient.ts");
      const index = await dailyAnalysisClient.read<Record<string, unknown>>("chart-index");
      files.push({ name: "index.json", payload: index });
      const scopes = Object.keys((index.scopes ?? {}) as Record<string, unknown>).sort();
      for (const scope of scopes) {
        const documentName = (index.scopes as Record<string, unknown>)[scope];
        if (documentName !== `chart-${scope}`) {
          errors.push({ resource: `chart-index:${scope}`, error: "invalid_chart_document_reference" });
          continue;
        }
        files.push({ name: `${scope}.json`, payload: await dailyAnalysisClient.read(String(documentName)) });
      }
      files.push({ name: "fetch-status.json", payload: { ...await dailyAnalysisClient.index(), source: "daily-analysis", fileCount: files.length, errors } });
      return files;
    }
    const indexPath = "public/data/housing/chart/index.json";
    const response = await fetch(`/api/shared-import?path=${encodeURIComponent(indexPath)}`, { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const index = await response.json() as Record<string, unknown>;
    files.push({ name: "index.json", payload: index });
    const scopes = Object.keys((index.scopes ?? {}) as Record<string, unknown>);
    await Promise.all(scopes.map(async (scope) => {
      const legacyPath = `public/data/housing/chart/${scope}.json`;
      const resource = `/api/shared-import?path=${encodeURIComponent(legacyPath)}`;
      try {
        const scopeResponse = await fetch(resource, { cache: "no-store" });
        if (!scopeResponse.ok) throw new Error(`HTTP ${scopeResponse.status}`);
        files.push({ name: `${scope}.json`, payload: await scopeResponse.json() });
      } catch (error) {
        errors.push({ resource, error: error instanceof Error ? error.message : String(error) });
      }
    }));
  } catch (error) {
    errors.push({ resource: "public/data/housing/chart/index.json", error: error instanceof Error ? error.message : String(error) });
  }
  files.push({ name: "fetch-status.json", payload: { collectedAt: new Date().toISOString(), fileCount: files.length, errors } });
  return files;
}

export async function downloadDiagnosticZip(args: {
  bundle: CoreDataBundle | null;
  status: DataLoadStatus;
  appVersion: string;
  selectedRange?: string;
  situationAnalysis?: SituationAnalysis;
}) {
  let h1bMetadataAudit: H1bEcosMetadataAudit | null = null;
  try {
    h1bMetadataAudit = await collectH1bEcosMetadataAudit();
  } catch (error) {
    h1bMetadataAudit = failedH1bMetadataAudit(error);
  }
  const housingChartFiles = await collectHousingChartDiagnostics();
  const blob = createDiagnosticZip({ ...args, h1bMetadataAudit, housingChartFiles });
  const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `financial-real-economy-shock-monitor_DIAGNOSTIC_v${args.appVersion}_${stamp}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 30_000);
}
